/**
 * „Zobrazit jako zákazník" — admin si prohlíží vlastní obchod
 * jako anonymní návštěvník (KAN-747).
 *
 * Ověření každé změny konfigurace znamenalo druhý tab a ruční
 * přepínání kontextů — a admin navíc storefront vidí jako přihlášený
 * uživatel, ne jako zákazník. To jsou různé stavy: jiná viditelnost,
 * jiná CTA. Diagnostika 2026-09-01 na tom strávila odpoledne.
 *
 * **Anonymita je skutečná, ne malovaná.** Nestačí schovat admin chrome
 * a tvářit se — server by pořád odpovídal přihlášenému adminovi.
 * V náhledu proto ``lib/api.ts`` posílá požadavky s
 * ``credentials: "omit"``: session cookie se neodešle, backend vidí
 * anonymní request a odpovídá tím, co uvidí zákazník. Nic se
 * nepředstírá na klientovi.
 *
 * **Košík ale musí fungovat**, jinak si tenant nákup nevyzkouší.
 * Cookie ``dxf_cart_session`` s ``omit`` odejít nemůže, takže náhled
 * jede po druhém, už existujícím transportu: handle v hlavičce
 * ``X-Guest-Session`` (viz ``core/guest_session.py`` — hlavička má
 * přednost před cookie).
 *
 * **Vlastní handle, ne ten z ``embed/guestSession``.** Ten bydlí v
 * ``localStorage`` a přežívá zavření tabu; vyrobit ho tady by adminovo
 * běžné prohlížení obchodu natrvalo přesunulo z cookie na hlavičku —
 * změna chování, o kterou nikdo nežádal. Handle náhledu proto žije v
 * ``sessionStorage`` a umře s tabem, stejně jako sám náhled.
 *
 * Náhled je **per tab**: tlačítko otevírá nový tab, takže admin
 * o rozdělanou práci nepřijde a zpátky se dostane zavřením tabu i
 * bannerem.
 */

/** Klíč, kterým se náhled zapíná z URL. */
export const CUSTOMER_PREVIEW_PARAM = "preview";

/** Hodnota, kterou ``?preview=`` musí nést. */
export const CUSTOMER_PREVIEW_VALUE = "customer";

const FLAG_KEY = "forgmatic.customer-preview";
const HANDLE_KEY = "forgmatic.customer-preview-cart";

/**
 * Cesty, na kterých se náhled neuplatní.
 *
 * Kdyby admin v tom tabu ručně přešel do adminu, ``credentials:
 * "omit"`` by mu tam vrátilo 401 na každý dotaz a vypadalo by to jako
 * odhlášení. Predikát se ptá na dokument, ne na uloženou hodnotu —
 * stejný tvar jako ``isEmbedDocument()``.
 */
const ADMIN_PATH_RE = /\/[a-z]{2}\/(admin|shopfloor|onboarding)(\/|$)/;

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Privátní režim / zablokované úložiště. Náhled se pak nezapne —
    // což je lepší než náhled, ze kterého nevede cesta ven.
    return null;
  }
}

function onAdminPath(): boolean {
  try {
    return ADMIN_PATH_RE.test(window.location.pathname);
  } catch {
    return false;
  }
}

/** Odkaz, který náhled otevírá. Domovská stránka obchodu, nic víc. */
export function customerPreviewHref(lang: string): string {
  return `/${lang}/?${CUSTOMER_PREVIEW_PARAM}=${CUSTOMER_PREVIEW_VALUE}`;
}

/** Běží v tomhle tabu náhled zákazníka? */
export function isCustomerPreview(): boolean {
  if (onAdminPath()) return false;
  try {
    return storage()?.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Přijmi ``?preview=customer`` z URL a ukliď parametr z adresního řádku.
 *
 * Vrací true, když se náhled tímhle voláním zapnul. Parametr se maže ze
 * stejného důvodu jako u ``?claim=``: nemá co viset v historii a v
 * ``Referer`` na všechno, na co stránka odkáže.
 */
export function adoptCustomerPreviewFromUrl(): boolean {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return false;
  }
  if (url.searchParams.get(CUSTOMER_PREVIEW_PARAM) !== CUSTOMER_PREVIEW_VALUE) {
    return false;
  }
  url.searchParams.delete(CUSTOMER_PREVIEW_PARAM);
  window.history.replaceState(
    window.history.state,
    "",
    url.pathname + url.search + url.hash,
  );
  if (onAdminPath()) return false;
  try {
    storage()?.setItem(FLAG_KEY, "1");
  } catch {
    return false;
  }
  return isCustomerPreview();
}

/** Ukonči náhled. Handle košíku jde s ním — patřil jen jemu. */
export function endCustomerPreview(): void {
  try {
    const store = storage();
    store?.removeItem(FLAG_KEY);
    store?.removeItem(HANDLE_KEY);
  } catch {
    // Úložiště, které odmítá mazat, odmítlo i zápis; není co mazat.
  }
}

/** Shape handle, který server přijme (``core/guest_session.py``). */
function mintHandle(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Handle košíku pro tenhle náhled, vyrobený při prvním použití.
 *
 * Volá se jen z ``lib/api.ts`` a jen když náhled běží — mimo něj by
 * tichý přesun z cookie na hlavičku byl změna chování bez zadání.
 */
export function currentPreviewCartHandle(): string | null {
  if (!isCustomerPreview()) return null;
  const store = storage();
  if (!store) return null;
  try {
    const existing = store.getItem(HANDLE_KEY);
    if (existing) return existing;
    const handle = mintHandle();
    store.setItem(HANDLE_KEY, handle);
    return handle;
  } catch {
    return null;
  }
}
